import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { useIsMobile } from '../../hooks/useIsMobile';
import Confetti from './Confetti';

const ETAPAS = [
  { n: 1, titulo: 'Escolha', texto: 'Selecione a consulta pública que deseja participar' },
  { n: 2, titulo: 'Responda', texto: 'Preencha o formulário com suas opiniões' },
  { n: 3, titulo: 'Contribua', texto: 'Sua opinião ajuda nas decisões da cidade' }
];

const INTERVALO_MS = 2600;

// "Como Funciona?" — depois da entrada inicial, as 3 etapas entram num ciclo
// automático e infinito: no desktop é um "farol" (uma etapa por vez fica em
// destaque, girando entre elas); no mobile — onde não cabem as 3 lado a lado
// — vira um cartão único que troca de conteúdo com um giro 3D (rotateY),
// nunca um carrossel lateral. Sempre que o ciclo chega na etapa 3, dispara
// confete. O ciclo só começa quando a seção entra na tela (IntersectionObserver).
const ComoFunciona = () => {
  const isMobile = useIsMobile();
  const containerRef = useRef(null);
  const [visivel, setVisivel] = useState(false);
  const [atual, setAtual] = useState(0);
  const [anterior, setAnterior] = useState(null);
  const [confettiKey, setConfettiKey] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setVisivel(entry.isIntersecting),
      { threshold: 0.35 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visivel) return undefined;
    const id = setInterval(() => {
      setAnterior(atual);
      setAtual((atual + 1) % ETAPAS.length);
    }, INTERVALO_MS);
    return () => clearInterval(id);
  }, [visivel, atual]);

  // Card antigo (giro de saída no mobile) só fica montado durante a
  // animação de saída, depois some pra não empilhar elementos.
  useEffect(() => {
    if (anterior === null) return undefined;
    const t = setTimeout(() => setAnterior(null), 650);
    return () => clearTimeout(t);
  }, [anterior]);

  useEffect(() => {
    if (atual === 2) setConfettiKey((k) => k + 1);
  }, [atual]);

  return (
    <div ref={containerRef} className="card bg-blue-50 border-2 border-blue-200 animate-fade-in-up mt-12">
      <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">
        Como Funciona?
      </h3>

      {isMobile ? (
        <div className="relative">
          <div className="relative h-40 mx-auto max-w-xs" style={{ perspective: '1000px' }}>
            {anterior !== null && (
              <EtapaCard key={`prev-${anterior}`} etapa={ETAPAS[anterior]} className="animate-flip-out" />
            )}
            <EtapaCard key={`curr-${atual}`} etapa={ETAPAS[atual]} className="animate-flip-in" />
            {atual === 2 && <Confetti triggerKey={confettiKey} />}
          </div>

          <div className="flex items-center justify-center gap-2 mt-2">
            {ETAPAS.map((etapa, i) => (
              <span
                key={etapa.n}
                className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                  i === atual ? 'bg-primary-600' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-6">
          {ETAPAS.map((etapa, i) => (
            <React.Fragment key={etapa.n}>
              <div className="group relative flex-1 text-center animate-fade-in-up" style={{ animationDelay: `${i * 0.15}s` }}>
                <div
                  key={i === 2 ? `badge-${confettiKey}` : undefined}
                  className={`relative w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 font-bold text-xl transition-all duration-500 group-hover:-translate-y-1 ${
                    i === atual
                      ? 'scale-125 bg-[#40A1E6] text-white shadow-lg shadow-[#40A1E6]/60 animate-beacon'
                      : 'scale-100 bg-primary text-white opacity-50'
                  } ${i === 2 && i === atual ? 'animate-badge-pop' : ''}`}
                >
                  {etapa.n}
                  {i === 2 && <Confetti triggerKey={confettiKey} />}
                </div>
                <h4 className={`font-semibold mb-2 transition-colors duration-500 ${i === atual ? 'text-gray-900' : 'text-gray-400'}`}>
                  {etapa.titulo}
                </h4>
                <p className={`text-sm transition-colors duration-500 ${i === atual ? 'text-gray-600' : 'text-gray-400'}`}>
                  {etapa.texto}
                </p>
              </div>

              {i < 2 && (
                <div className="hidden sm:flex items-center justify-center pt-3">
                  <ArrowRight className="w-5 h-5 text-primary-300 animate-flow-arrow" />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

const EtapaCard = ({ etapa, className }) => (
  <div className={`absolute inset-0 flex flex-col items-center justify-center text-center ${className}`} style={{ backfaceVisibility: 'hidden' }}>
    <div className={`w-14 h-14 rounded-full bg-[#40A1E6] text-white flex items-center justify-center mb-3 font-bold text-2xl shadow-lg shadow-[#40A1E6]/50 ${etapa.n === 3 ? 'animate-badge-pop' : ''}`}>
      {etapa.n}
    </div>
    <h4 className="font-semibold text-gray-900 mb-1">{etapa.titulo}</h4>
    <p className="text-sm text-gray-600 px-4">{etapa.texto}</p>
  </div>
);

export default ComoFunciona;
